import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Inject, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatLegacySliderChange as MatSliderChange } from '@angular/material/legacy-slider';
import { CAMERA_REF } from './camera-ref_token';
import { CameraRef } from './camera-ref';
import { TranslateService } from '@ngx-translate/core';

export enum Rotation {
  TOP,
  LEFT,
  BOTTOM,
  RIGHT
}

declare const faceapi: any;
declare const navigator: any;

@Component({
  selector: 'camera',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './camera.component.html',
  styleUrls: ['./camera.component.scss']
})
export class CameraComponent implements OnInit, OnDestroy {

  @ViewChild('canvas')
  public canvas: ElementRef;
  @ViewChild('canvasDetect')
  public canvasDetect: ElementRef;
  public video: HTMLVideoElement;
  public canTorch: boolean;
  public torchOn: boolean;
  public canZoom: boolean;
  public zoomMax: number;
  public zoomMin: number;
  public zoomStep: number;
  public zoomValue: number;
  public canSwapCamera: boolean;
  public pictureData: string;
  // private video: HTMLVideoElement;
  private stream: MediaStream;
  private videoDevices: any[];
  private videoDeviceId: string;
  private nextVideoDevice: number = 0;
  private rotation: number = 0;
  enableCapture: boolean = false;
  private animationFrameId: number;
  private countDetections: number = 0;
  private detections: any[];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(CAMERA_REF) private cameraRef: CameraRef,
    private ref: ChangeDetectorRef
  ) {
    this.video = this.createInMemoryVideoElement(this.document);
    this.loadSettings();
  }

  public ngOnInit(): void {
    faceapi.nets.tinyFaceDetector.loadFromUri("assets/models")
    this.loadSettings();
    if (this.videoDeviceId && !this.isSafari()) {
      this.setupStream(this.videoDeviceId);
    } else {
      this.setupStream();
    }
  }

  public ngOnDestroy(): void {
    this.saveSettings();
    this.cleanUp();
  }

  public toggleTorch(): void {
    if (this.torchOn) {
      // @ts-ignore
      this.stream.getVideoTracks()[0].applyConstraints({ advanced: [{ torch: false }] });
      this.torchOn = false;
    } else {
      // @ts-ignore
      this.stream.getVideoTracks()[0].applyConstraints({ advanced: [{ torch: true }] });
      this.torchOn = true;
    }
  }

  public async swapCamera(): Promise<void> {
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function' && !this.isSafari()) {
      if (!this.videoDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.videoDevices = devices.filter(device => device.kind == 'videoinput');
      }
      const index = this.nextVideoDevice % this.videoDevices.length;
      this.videoDeviceId = this.videoDevices[index].deviceId;
      this.cleanUp();
      this.setupStream(this.videoDeviceId);
      this.nextVideoDevice++;
    }
  }

  public rotate() {
    const numberOfPossibleRotationPositions = Object.keys(Rotation).length / 2;
    this.rotation = (this.rotation + 1) % numberOfPossibleRotationPositions;
  }

  public onZoomChange({ value }: MatSliderChange): void {
    // @ts-ignore
    this.stream.getVideoTracks()[0].applyConstraints({ advanced: [{ zoom: value }] });
  }

  public takePicture(): void {
    if(this.enableCapture){
      const canvas: HTMLCanvasElement = this.document.createElement('canvas');
      canvas.width = this.canvasDetect.nativeElement.width;
      canvas.height = this.canvasDetect.nativeElement.height;
      canvas.getContext('2d').drawImage(this.canvasDetect.nativeElement, 0, 0, canvas.width, canvas.height);
      this.pictureData = canvas.toDataURL('image/jpeg');
    }
  }

  public retake(): void {
    this.pictureData = null;
  }

  public use(): void {
    this.cameraRef.close(this.pictureData);
  }

  public close(): void {
    this.cameraRef.close();
  }

  public setEnableCapture(enable): void {
    this.enableCapture = enable
    this.ref.detectChanges()
  }

  private async setupStream(deviceId?: string): Promise<void> {
    let constraints: MediaStreamConstraints;
    if (deviceId) {
      constraints = { video: { deviceId ,
        //width: { ideal: window.innerWidth },
        height: { ideal: window.innerHeight }
      }
      };
    } else {
      constraints = { video: { facingMode: 'environment' ,
      //width: { ideal: window.innerWidth },
      height: { ideal: window.innerHeight }
    }
    };
    }
    navigator.getMedia =
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia ||
      navigator.getUserMedia;
    navigator.getMedia(
      constraints,
      async (stream) => {
        this.stream = stream;
        if (this.stream) {
          // start video
          this.stopCanvas();
          this.video.onloadedmetadata = (e) => {
            const canvasDetect: HTMLCanvasElement = this.canvasDetect.nativeElement;
            canvasDetect.style.zIndex="1"
            const canvas: HTMLCanvasElement = this.canvas.nativeElement;
            canvas.style.zIndex="2"
            const displaySize = { width: this.video.videoWidth, height: this.video.videoHeight };
            faceapi.matchDimensions(canvas, displaySize);
            this.startCanvas(canvas, canvasDetect, this.video);
            this.setupControls(this.stream.getVideoTracks()[0])
          }
          this.addStreamToVideo(this.stream, this.video);
        }
      },
      (error) => {
        if (deviceId) {
          // if failed with device id try again without
          this.setupStream();
        } else {
          this.cameraRef.reject(error);
        }
      }
    );
  }

  private async updateCanvas(canvas: HTMLCanvasElement, canvasDetect: HTMLCanvasElement, video: HTMLVideoElement) {
    if(!canvas || !video.videoWidth){
      return
    }
    const context: CanvasRenderingContext2D = canvas.getContext('2d');
    const contextDetect: CanvasRenderingContext2D = canvasDetect.getContext('2d');
    switch (this.rotation) {
      case Rotation.TOP:
      default:
        canvasDetect.width = video.videoWidth;
        canvasDetect.height = video.videoHeight;
        break;
      case Rotation.LEFT:
        canvasDetect.width = video.videoHeight;
        canvasDetect.height = video.videoWidth;
        contextDetect.transform(0, -1, 1, 0, 0, video.videoWidth);
        break;
      case Rotation.BOTTOM:
        canvasDetect.width = video.videoWidth;
        canvasDetect.height = video.videoHeight;
        contextDetect.transform(-1, 0, 0, -1, video.videoWidth, video.videoHeight);
        break;
      case Rotation.RIGHT:
        canvasDetect.width = video.videoHeight;
        canvasDetect.height = video.videoWidth;
        contextDetect.transform(0, 1, -1, 0, video.videoHeight, 0);
        break;
    }
    contextDetect.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    if(this.countDetections%30==0){
      this.detections = await faceapi.detectAllFaces(canvasDetect, new faceapi.TinyFaceDetectorOptions())
    }
    this.countDetections++

    context.clearRect(0, 0, canvas.width, canvas.height);
    switch (this.rotation) {
      case Rotation.TOP:
      default:
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        break;
      case Rotation.LEFT:
        canvas.width = video.videoHeight
        canvas.height = video.videoWidth
        break;
      case Rotation.BOTTOM:
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        break;
      case Rotation.RIGHT:
        canvas.width = video.videoHeight
        canvas.height = video.videoWidth
        break;
    }
    const displaySize = { width: canvas.width, height: canvas.height };
    const resizedDetections = faceapi.resizeResults(this.detections, displaySize);
    faceapi.draw.drawDetections(canvas, resizedDetections);

    context.beginPath();
    context.rect(displaySize.width, 0, -displaySize.width, displaySize.height);
    context.ellipse(displaySize.width/2, displaySize.height/2, displaySize.height/4, displaySize.height/3, 0, 0, 2 * Math.PI);
    context.fillStyle = "rgba(0, 0, 0, 0.5)";
    context.fill();
    const limite = displaySize.height*1/16


    context.beginPath();
    context.rect(displaySize.width/2-limite, displaySize.height/2, limite*2, limite*3);
    context.strokeStyle = "#FF0000";
    context.stroke();


    if(this.detections.length > 0 ){
      const resizedDetections = faceapi.resizeResults(this.detections, displaySize);
      const frame_width = resizedDetections[0]._box._width
      const frame_height = resizedDetections[0]._box._height
      const frame_x = resizedDetections[0]._box._x + frame_width / 2
      const frame_y = resizedDetections[0]._box._y + frame_height / 2

      context.beginPath();
      context.ellipse(frame_x, frame_y, 5, 5, 0, 0, 2 * Math.PI);
      context.fillStyle = "rgba(0, 0, 1, 1)";
      context.fill();

        if ( frame_x > displaySize.width/2 - limite && frame_x < displaySize.width/2 + limite &&
             frame_y > displaySize.height/2 && frame_y < displaySize.height/2 + limite*3 &&
             frame_width > displaySize.height / 4 && frame_height > displaySize.height / 3 ){
                this.setEnableCapture(true)
                context.beginPath();
                context.ellipse(displaySize.width/2, displaySize.height/2, displaySize.height/4, displaySize.height/3, 0, 0, 2 * Math.PI);
                context.strokeStyle = "#00FF00";
                context.stroke();
        } else this.setEnableCapture(false)

    }else this.setEnableCapture(false)

  }

  private startCanvas(canvas: HTMLCanvasElement, canvasDetect: HTMLCanvasElement, video: HTMLVideoElement): void {
    const frame = () => {
      this.updateCanvas(canvas, canvasDetect, video);
      this.animationFrameId = requestAnimationFrame(frame);
    }
    this.animationFrameId = requestAnimationFrame(frame);
  }

  private stopCanvas(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  private addStreamToVideo(stream: MediaStream, video: HTMLVideoElement): void {
    if ('srcObject' in video) {
      video.srcObject = stream;
    } else {
      // @ts-ignore
      video.src = URL.createObjectURL(stream);
    }
    video.play().catch(() => {
      // Autoplay blocked by browser policy (e.g. Chrome Android without user gesture).
      // The video has autoplay + muted attributes as fallback; nothing else to do.
    });
  }

  private setupControls(track: MediaStreamTrack): void {
    if (track && typeof track.getCapabilities === 'function') {
      setTimeout(() => {
        const capabilities = track.getCapabilities();
        this.canTorch = (capabilities as any).torch;
        this.canZoom = (capabilities as any).zoom;
        if (this.canZoom) {
          this.zoomMax = (capabilities as any).zoom.max;
          this.zoomMin = (capabilities as any).zoom.min;
          this.zoomStep = (capabilities as any).zoom.step;
        }
        const settings = track.getSettings();
        this.torchOn = (settings as any).torch;
        this.zoomValue = (settings as any).zoom;
      }, 1000);
    }
    if (navigator.mediaDevices && typeof navigator.mediaDevices.enumerateDevices === 'function' && !this.isSafari()) {
      this.canSwapCamera = true;
      this.ref.detectChanges()
    }
  }

  private stopStream(stream: MediaStream) {
    // @ts-ignore
    if (typeof stream.stop == 'function') {
      // @ts-ignore
      stream.stop();
    }
    stream.getTracks().forEach(track => {
      track.stop();
    })
  }

  private cleanUp(): void {
    this.stopCanvas();
    if (this.video.src) {
      URL.revokeObjectURL(this.video.src);
    }
    if (this.stream) {
      this.stopStream(this.stream);
    }
  }

  private createInMemoryVideoElement(document: Document): HTMLVideoElement {
    const video: HTMLVideoElement = document.createElement('video');
    video.setAttributeNode(document.createAttribute('playsinline'));
    video.autoplay = true;
    video.muted = true;
    return video;
  }

  private saveSettings(): void {
    if (this.rotation != null) {
      localStorage.setItem('camera-rotation', JSON.stringify(this.rotation));
    }
    if (this.videoDeviceId != null && !this.isSafari()) {
      localStorage.setItem('camera-device-id', JSON.stringify(this.videoDeviceId));
    }
  }

  private loadSettings(): void {
    const rotation = JSON.parse(localStorage.getItem('camera-rotation'));
    const videoDeviceId = JSON.parse(localStorage.getItem('camera-device-id'));
    if (rotation != null) {
      this.rotation = rotation;
    }
    if (videoDeviceId != null && !this.isSafari()) {
      this.videoDeviceId = videoDeviceId;
    }
  }

  private isSafari(): boolean {
    return new RegExp('^((?!chrome|android).)*safari', 'i').test(navigator.userAgent);
  }

}
